import React, { useState } from 'react'
import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import sortBy from 'lodash/sortBy'
import ReportTable from '../../components/reports/table'
import ReportFilter from '../../components/reports/filters'
import ReportPeriodFilter from '../../components/reports/filter-period'
import ReportDisplay from '../../components/reports/display'
import { ILeaveBalanceReportProps } from './types'
import { leaveBalanceReportColumns, ReportPeriod } from '@vacationtracker/shared/types/export'
import BalancePeriod from './balance-period'
import IntlMessages from '../../util/IntlMessages'
import { UserStatus } from '@vacationtracker/shared/types/user'
import capitalize from 'lodash/capitalize'

const LeaveBalanceReport: React.FC<ILeaveBalanceReportProps> = ({
  companyData,
  defaultDates,
  calculationStartYear,
  data,
  total,
  page,
  loading,
  display,
  defaultFilters,
  onFiltersChange,
  onPeriodChange,
  onDisplayChange,
  loadNext,
  loadPrevious,
  download,
}) => {

  const [showPeriodFilters, setShowPeriodFilters] = useState<boolean>(false)
  const [showYtdDatePicker, setShowYtdDatePicker] = useState<boolean>(false)
  const [hideEmptyColumns, setHideEmptyColumns] = useState<boolean>(() => {
    const stored = localStorage.getItem('leave-balance-report_hideEmptyColumns')
    if (stored !== null) return stored === 'true'
    localStorage.setItem('leave-balance-report_hideEmptyColumns', 'true')
    return true
  })

  const handleHideEmptyColumnsChange = (value: boolean) => {
    setHideEmptyColumns(value)
    localStorage.setItem('leave-balance-report_hideEmptyColumns', String(value))
  }

  const filters = [
    { type: 'locationIds', options: companyData.getLocationList, label: 'Locations' },
    { type: 'departmentIds', options: companyData.getTeamList, label: 'Departments'},
    { type: 'labelIds', options: companyData.getLabels, label: 'Labels' },
    { type: 'userIds', options: companyData.getUsersFiltered.users, label: 'Users' },
    { type: 'leaveTypeIds', options: sortBy(companyData.getLeaveTypeList, 'position'), label: 'Leave Types' },
    { 
      type: 'userStatuses',
      options: (['ACTIVE', 'INACTIVE', 'DELETED'] as UserStatus[]).map(status => ({ id: status, name: capitalize(status) })),
      label: 'User Status',
    },
  ]

  const onBalancePeriodChange = (period: ReportPeriod) => {
    setShowPeriodFilters(period === 'CUSTOM_DAYS')
    setShowYtdDatePicker(period === 'YEAR_TO_DATE')
    if (period === 'YEAR_TO_DATE') {
      onPeriodChange([dayjs().startOf('year'), dayjs()], period)
    } else {
      onPeriodChange(defaultDates, period)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
        <BalancePeriod onChange={onBalancePeriodChange} />
        {showYtdDatePicker && (
          <>
            <span style={{ marginLeft: '8px' }}><IntlMessages id="export.yearToDateAsOf" /></span>
            <DatePicker
              defaultValue={dayjs()}
              format="YYYY-MM-DD"
              allowClear={false}
              disabledDate={(current) => {
                const maxDate = dayjs().add(1, 'year').endOf('year')
                if (current.isAfter(maxDate, 'day')) return true
                if (calculationStartYear) {
                  const minDate = dayjs(calculationStartYear, 'YYYY').startOf('year')
                  if (current.isBefore(minDate, 'day')) return true
                }
                return false
              }}
              onChange={(date: Dayjs | null) => {
                if (date) {
                  onPeriodChange([dayjs().startOf('year'), date], 'YEAR_TO_DATE')
                }
              }}
              style={{ marginLeft: '8px' }}
            />
          </>
        )}
      </div>
      {showPeriodFilters && <ReportPeriodFilter dateFormat={'YYYY-MM-DD'} defaultDates={defaultDates} onFilterChange={onPeriodChange}/>}
      <ReportDisplay defaultDisplay={display} onDisplayChange={onDisplayChange}/>
      <ReportFilter filters={filters} onFilterChange={onFiltersChange} defaultSelectedValues={defaultFilters}/>
      <ReportTable
        columnNames={leaveBalanceReportColumns}
        data={data}
        total={total}
        loadNext={loadNext}
        loadPrevious={loadPrevious}
        download={download}
        loading={loading}
        dateFormat={'YYYY-MM-DD'}
        display={display}
        page={page}
        reportType='leave-balance-report'
        editableColumns={true}
        hideEmptyColumns={hideEmptyColumns}
        onHideEmptyColumnsChange={handleHideEmptyColumnsChange}
      />
    </div>
  )
}

export default LeaveBalanceReport
